import { useState, useCallback } from 'react';
import styles from './Interactive.module.css';

interface SequenceConfig {
  items: string[];
}

interface SequenceProps {
  config: SequenceConfig;
  onSubmit: (answer: string) => void;
  disabled: boolean;
  showResult: boolean;
}

export function Sequence({ config, onSubmit, disabled, showResult }: SequenceProps) {
  // Shuffle items initially
  const [items, setItems] = useState<string[]>(() => {
    const shuffled = [...config.items];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  });

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = useCallback((index: number) => {
    if (disabled) return;
    setDraggedIndex(index);
  }, [disabled]);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (disabled) return;
    setDragOverIndex(index);
  }, [disabled]);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((dropIndex: number) => {
    if (disabled || draggedIndex === null) return;

    setItems((prev) => {
      const newItems = [...prev];
      const [draggedItem] = newItems.splice(draggedIndex, 1);
      newItems.splice(dropIndex, 0, draggedItem);
      return newItems;
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [disabled, draggedIndex]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleSubmit = () => {
    if (disabled) return;
    onSubmit(JSON.stringify(items));
  };

  const getItemClass = (index: number) => {
    let className = styles.sequenceItem;

    if (disabled) {
      className += ` ${styles.disabled}`;
    }

    if (draggedIndex === index) {
      className += ` ${styles.dragging}`;
    }

    if (dragOverIndex === index && draggedIndex !== index) {
      className += ` ${styles.dragOver}`;
    }

    if (showResult) {
      // Check if this item is in the correct position
      if (items[index] === config.items[index]) {
        className += ` ${styles.correct}`;
      } else {
        className += ` ${styles.incorrect}`;
      }
    }

    return className;
  };

  return (
    <div className={styles.sequenceContainer}>
      <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
        Drag items to reorder them:
      </p>

      {items.map((item, index) => (
        <div
          key={`${item}-${index}`}
          className={getItemClass(index)}
          draggable={!disabled}
          onDragStart={() => handleDragStart(index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragLeave={handleDragLeave}
          onDrop={() => handleDrop(index)}
          onDragEnd={handleDragEnd}
        >
          <span className={styles.sequenceNumber}>{index + 1}</span>
          <span className={styles.sequenceText}>{item}</span>
          {!disabled && <span className={styles.dragHandle}>⋮⋮</span>}
        </div>
      ))}

      {!showResult && (
        <div className={styles.inputContainer}>
          <button
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={disabled}
          >
            Submit Order
          </button>
        </div>
      )}
    </div>
  );
}
